import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useRef, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { FaPhoneAlt, FaCheckCircle, FaHardHat, FaClipboardCheck, FaCamera, FaPaperPlane, FaPencilAlt, FaTrash } from "react-icons/fa";
import {
  listOrders,
  listContracts,
  listContractorInvoices,
  createOrder,
  updateOrder,
  deleteOrder,
  autoCreateOrders,
  getAutoCreateCheck,
  getCombineCandidates,
  combineOrders,
  getClaim,
  updateClaim,
  listEntityRows,
  reorderList,
} from "../../services/apiClient.js";
import { SortableRows, SortableTr } from "../SortableRows.jsx";
import GrandTotals from "./GrandTotals.jsx";
import ContractsPanel from "./ContractsPanel.jsx";
import InvoicesPanel from "./InvoicesPanel.jsx";
import DoAndChargePanel from "./DoAndChargePanel.jsx";
import Hint from "./../shared/Hint.jsx";
import { useNavigate } from "react-router-dom";
import TabHeader from "./TabHeader.jsx";

// Orders — the payable side. Auto-create turns the quote into one order per work
// centre; each order's Contract Given (committed) comes from its contract lines and
// Invoices Received (actual) from contractor invoices (Stage 2). Balance = given −
// received; overrun flags when received exceeds the budget.

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
const money = (v) => nzd.format(Number(v || 0));

const EMPTY = {
  work_centre_id: "",
  contractor_company_id: "",
  comments: "",
  work_complete: false,
  health_safety_na: false,
  risk_assessment_uploaded: false,
  photos_uploaded: false,
  work_sheet_sent: false,
};

// Legacy colours: TBD until both Contract Given and Quoted Allowance exist; then
// On Target while invoices ≤ contract given; orange while ≤ quoted allowance;
// red beyond the allowance — UNLESS the overrun has been checked (check_overrun
// off while still over): 1.0 downgraded that to orange "(Checked)"
// (OrdersPage.aspx.cs:790) so an approved overrun stops shouting.
function overrun(o) {
  const given = Number(o.contract_given || 0);
  const received = Number(o.invoices_received || 0);
  const allowance = Number(o.quoted_allowance || 0);
  if (given <= 0 || allowance <= 0) return { variant: "secondary", text: "TBD" };
  if (received <= given) return { variant: "success", text: "On Target" };
  const over = received - given;
  if (received > allowance) {
    return o.check_overrun
      ? { variant: "danger", text: `${money(over)} over` }
      : { variant: "warning", text: `${money(over)} over (Checked)` };
  }
  return { variant: "warning", text: `${money(over)} over` };
}

// 1.0 painted the order number red while an overrun awaited checking
// (admin-only there; shown to everyone here — PCOs chase these too).
const numCls = (o) => (o?.check_overrun ? "text-danger fw-semibold" : "text-muted");

// The 1.0 "Invoices From Contractor" header block: the order's key facts above
// its invoices (Karen 2026-07-16 — "they will want that").
function OrderHeader({ o }) {
  const balance = Number(o.contract_given || 0) - Number(o.invoices_received || 0);
  const ov = overrun(o);
  const flag = (v) =>
    v ? <FaCheckCircle className="text-success" /> : <span className="text-danger fw-bold">✗</span>;
  const Item = ({ label, children }) => (
    <Col xs={6} md={3} className="py-1">
      <span className="text-muted small me-2">{label}</span>
      <span className="small fw-semibold">{children}</span>
    </Col>
  );
  return (
    <div className="border rounded px-3 py-2 mb-3 bg-body-tertiary">
      <Row className="g-0">
        {/* The GLOBAL order number (numbered like claims/invoices — legacy
            OrderNumber continues); seq is just the list position. */}
        <Item label="Order #">{o.order_number ?? o.seq}</Item>
        <Item label="Work Centre">{o.work_centre_name || "—"}</Item>
        <Item label="Contractor">{o.contractor_name || "—"}</Item>
        {/* Contractor contact (legacy GPSCompanyContact): auto-picked — the
            contractor's Primary (Tender Primary on tender jobs), as 1.0. */}
        <Item label="Contact">
          {o.contractor_contact_name ? (
            <>
              {o.contractor_contact_name}
              {o.contractor_contact_phone ? ` · ${o.contractor_contact_phone}` : ""}
              {o.contractor_contact_email ? (
                <>
                  {" · "}
                  <a href={`mailto:${o.contractor_contact_email}`}>{o.contractor_contact_email}</a>
                </>
              ) : null}
            </>
          ) : (
            "—"
          )}
        </Item>
        <Item label="Over Run">
          <Badge bg={ov.variant} text={ov.variant === "warning" ? "dark" : undefined}>{ov.text}</Badge>
        </Item>
        <Item label="Quoted Allowance">{money(o.quoted_allowance)}</Item>
        <Item label="Contract Given">{money(o.contract_given)}</Item>
        <Item label="Invoices Received">{money(o.invoices_received)}</Item>
        <Item label="Balance Outstanding">{money(balance)}</Item>
        <Item label="Work Complete">{flag(o.work_complete)}</Item>
        <Item label="H&S N/A">{flag(o.health_safety_na)}</Item>
        <Item label="Risk Assessment">{flag(o.risk_assessment_uploaded)}</Item>
        <Item label="Photos">{flag(o.photos_uploaded)}</Item>
        <Item label="Work Sheet Sent">{flag(o.work_sheet_sent)}</Item>
        <Item label="Comments">{o.comments || "—"}</Item>
      </Row>
    </div>
  );
}

function ClaimOrdersTab({ claimId, onChanged }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [lookups, setLookups] = useState({ "work-centres": [], "contractor-companies": [] });
  const [editingId, setEditingId] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY);
  // Sub-PAGE within the tab (Karen/Robert 2026-07-16 — 1.0's popups): click the
  // money (or the contract icon) and the tab shows JUST that order's contracts
  // or invoices; Back/Close returns to the list at the same scroll position.
  const [subview, setSubview] = useState(null); // null | { orderId, kind: "contracts"|"invoices" }
  const savedScroll = useRef(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [autoCheck, setAutoCheck] = useState(null); // legacy auto-create eligibility
  const [autoCreating, setAutoCreating] = useState(false);
  const autoCreatePending = useRef(false);
  const [combinable, setCombinable] = useState([]); // contractors with mergeable orders
  const [combineWith, setCombineWith] = useState("");
  const [orderComments, setOrderComments] = useState(""); // job-level, legacy AdditionalOrderComments
  const [orderCommentsLoaded, setOrderCommentsLoaded] = useState(false); // gate on blur-save — never write over unloaded text
  // Show Order Break Down (legacy checkbox — Karen runs with it ON): each
  // order's contract lines listed read-only under its row.
  const [showBreakdown, setShowBreakdown] = useState(() => {
    try { return localStorage.getItem("orders:breakdown") !== "0"; } catch { return true; }
  });
  const [breakdown, setBreakdown] = useState({}); // orderId -> { contracts, invoices }
  const [totalsTick, setTotalsTick] = useState(0); // re-pull GrandTotals after changes

  function load() {
    return listOrders(claimId)
      .then((rows) => {
        setOrders(rows);
        setTotalsTick((t) => t + 1);
        getAutoCreateCheck(claimId).then(setAutoCheck).catch(() => {});
        getCombineCandidates(claimId).then(setCombinable).catch(() => {});
        loadBreakdown(rows);
      })
      .catch(() => setError("Unable to load orders."));
  }

  // Fetch every order's contract lines + contractor invoices for the breakdown
  // (small lists; parallel per order via the existing endpoints).
  async function loadBreakdown(rows) {
    const pairs = await Promise.all(
      rows.map(async (o) => {
        const [contracts, invoices] = await Promise.all([
          listContracts(claimId, o.id).catch(() => []),
          listContractorInvoices(claimId, o.id).catch(() => []),
        ]);
        return [o.id, { contracts, invoices }];
      })
    );
    setBreakdown(Object.fromEntries(pairs));
  }

  function toggleBreakdown(on) {
    setShowBreakdown(on);
    try { localStorage.setItem("orders:breakdown", on ? "1" : "0"); } catch { /* ignore */ }
  }
  useEffect(() => {
    load();
    // The response is an ENVELOPE — { claim, ...status } — and reading the
    // field off the top level meant the box ALWAYS started empty and a
    // focus+blur wiped the stored comments (save-on-blur writes "" back).
    // The loaded flag is the second half of the fix: until a load has
    // actually landed, blur must not save at all.
    setOrderCommentsLoaded(false);
    getClaim(claimId)
      .then(({ claim: c }) => {
        setOrderComments(c?.additional_order_comments || "");
        setOrderCommentsLoaded(true);
      })
      .catch(() => {});
    Promise.all(
      ["work-centres", "contractor-companies"].map((k) =>
        listEntityRows(k).then((r) => [k, r]).catch(() => [k, []])
      )
    ).then((pairs) => setLookups(Object.fromEntries(pairs)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  async function saveOrderComments() {
    // A blur before the stored text has loaded (or after a failed load) must
    // not save — it would write the empty box over real comments.
    if (!orderCommentsLoaded) return;
    try {
      await updateClaim(claimId, { additional_order_comments: orderComments });
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save order comments.");
    }
  }

  async function combine() {
    if (!combineWith) return;
    const name = combinable.find((c) => String(c.contractor_company_id) === String(combineWith))?.contractor_name;
    if (!window.confirm(`Combine all of ${name}'s orders (without invoices) into their first order?`)) return;
    setError("");
    try {
      const r = await combineOrders(claimId, combineWith);
      setNotice(`Combined ${r.combined} order(s) into one.`);
      setCombineWith("");
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to combine orders.");
    }
  }

  const set = (patch) => setForm((c) => ({ ...c, ...patch }));
  // Breakdown borrows the Comments column's width (Robert 2026-07-16).
  const showComments = !showBreakdown;
  const totals = orders.reduce(
    (a, o) => {
      a.allowance += Number(o.quoted_allowance || 0);
      a.given += Number(o.contract_given || 0);
      a.received += Number(o.invoices_received || 0);
      return a;
    },
    { allowance: 0, given: 0, received: 0 }
  );

  async function autoCreate() {
    if (autoCreatePending.current) return;
    autoCreatePending.current = true;
    setAutoCreating(true);
    setError("");
    setNotice("");
    try {
      const r = await autoCreateOrders(claimId);
      let msg = `Created ${r.orders_created} order(s) and ${r.contracts_created} contract line(s) from the quote.`;
      if (r.lines_without_work_centre > 0) {
        msg += ` ${r.lines_without_work_centre} quote line(s) had no work centre and were skipped.`;
      }
      if (r.orders_created === 0 && r.contracts_created === 0) {
        msg = "Nothing to create — every quote line with a work centre is already on an order.";
      }
      setNotice(msg);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to auto-create orders.");
    } finally {
      autoCreatePending.current = false;
      setAutoCreating(false);
    }
  }

  function startAdd() {
    setError("");
    setForm(EMPTY);
    setEditingId("new");
  }
  function startEdit(o) {
    setError("");
    setForm({
      work_centre_id: o.work_centre_id || "",
      contractor_company_id: o.contractor_company_id || "",
      comments: o.comments || "",
      work_complete: !!o.work_complete,
      health_safety_na: !!o.health_safety_na,
      risk_assessment_uploaded: !!o.risk_assessment_uploaded,
      photos_uploaded: !!o.photos_uploaded,
      work_sheet_sent: !!o.work_sheet_sent,
    });
    setEditingId(o.id);
  }
  async function save() {
    setError("");
    try {
      if (editingId === "new") {
        const created = await createOrder(claimId, form);
        // Straight into the contract lines — allowance + contract given are
        // entered THERE (derived on the order), so a new order never dead-ends
        // (Karen 2026-07-15: "nowhere to enter quoted allowance").
        if (created?.id) openSubview(created.id, "contracts");
      } else {
        await updateOrder(claimId, editingId, form);
      }
      setEditingId(null);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save order.");
    }
  }
  async function remove(o) {
    if (!window.confirm(`Delete order #${o.order_number ?? o.seq}? Its contract lines go too.`)) return;
    try {
      await deleteOrder(claimId, o.id);
      if (subview?.orderId === o.id) setSubview(null);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete order.");
    }
  }

  function openSubview(orderId, kind) {
    savedScroll.current = window.scrollY;
    setSubview({ orderId, kind });
    window.scrollTo({ top: 0 });
  }
  function closeSubview() {
    setSubview(null);
    requestAnimationFrame(() => window.scrollTo(0, savedScroll.current));
  }

  // Drag-and-drop: the grid shows the GLOBAL order_number (Karen 2026-07-19);
  // seq is drag-position only and no longer displayed. Reload after saving so
  // any seq-derived state refreshes (optimistic move first, revert on failure).
  const dragDisabled = editingId !== null;
  async function handleReorder(reordered) {
    const prev = orders;
    setOrders(reordered);
    try {
      await reorderList(claimId, "orders", reordered.map((o) => o.id));
      await load();
    } catch (err) {
      setOrders(prev);
      setError(err.response?.data?.error || "Unable to save the new order.");
    }
  }

  const FLAGS = [
    ["work_complete", "Work complete", FaCheckCircle],
    ["health_safety_na", "H&S N/A", FaHardHat],
    ["risk_assessment_uploaded", "Risk assessment", FaClipboardCheck],
    ["photos_uploaded", "Photos", FaCamera],
    ["work_sheet_sent", "Work sheet sent", FaPaperPlane],
  ];

  // INLINE editor (Karen meeting 2026-07-16): the edit row keeps the table's
  // columns — selects where the names were, the derived money stays visible,
  // the checklist is the same icons (click to toggle, hover to see what it is).
  // Quoted Allowance / Contract Given are NOT edited here — they're derived
  // from the contract lines (legacy rule); click the money to open them.
  function editorRow(key) {
    const o = key === "new" ? null : orders.find((x) => x.id === key);
    const balance = o ? Number(o.contract_given || 0) - Number(o.invoices_received || 0) : 0;
    const ov = o ? overrun(o) : null;
    return [
      <tr key={key} className="table-active">
        <td />
        <td className={o ? numCls(o) : "text-muted"}
            data-hint={o?.check_overrun ? "Red = this order has an overrun awaiting checking (contractor invoices exceed the contract given). Tick Overrun Checked once reviewed." : undefined}>
          {o ? (o.order_number ?? o.seq) : "new"}
        </td>
        <td>
          <ThemedSelect size="sm" value={form.work_centre_id} onChange={(e) => set({ work_centre_id: e.target.value })}>
            <option value="">— Work Centre —</option>
            {(lookups["work-centres"] || []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </ThemedSelect>
        </td>
        <td>
          <ThemedSelect size="sm" value={form.contractor_company_id} onChange={(e) => set({ contractor_company_id: e.target.value })}>
            <option value="">— Contractor —</option>
            {(lookups["contractor-companies"] || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </ThemedSelect>
        </td>
        <td className="text-end text-nowrap text-muted" data-hint="Derived from the contract lines — click the money on the saved row to edit them">
          {o ? money(o.quoted_allowance) : "—"}
        </td>
        <td className="text-end text-nowrap text-muted">{o ? money(o.contract_given) : "—"}</td>
        <td className="text-end text-nowrap text-muted">{o ? money(o.invoices_received) : "—"}</td>
        <td className="text-end text-nowrap text-muted">{o ? money(balance) : "—"}</td>
        <td>{ov ? <Badge bg={ov.variant} text={ov.variant === "warning" ? "dark" : undefined}>{ov.text}</Badge> : null}</td>
        <td className="text-nowrap">
          {FLAGS.map(([k, label, Icon]) => (
            <Hint key={k} label={label}>
              <span
                role="button"
                className={`me-2 ${form[k] ? "text-success" : "text-body-tertiary opacity-50"}`}
                onClick={() => set({ [k]: !form[k] })}
              >
                <Icon />
              </span>
            </Hint>
          ))}
        </td>
        {showComments ? (
          <td>
            <Form.Control size="sm" placeholder="Comments" value={form.comments} onChange={(e) => set({ comments: e.target.value })} />
          </td>
        ) : null}
        <td className="text-end">
          {/* Words, not icons: ✓/✗ beside a row of checklist ticks is a
              misclick factory. Stacked so the column width never jumps. */}
          <div className="d-grid gap-1">
            <Button size="sm" onClick={save}>{key === "new" ? "Add" : "Save"}</Button>
            <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>Cancel</Button>
          </div>
        </td>
      </tr>,
      ...(showComments
        ? []
        : [
            <tr key={`${key}-comments`}>
              <td colSpan={11} className="bg-body-tertiary py-1">
                <div className="d-flex align-items-center gap-2">
                  <span className="small text-muted text-nowrap">Comments</span>
                  <Form.Control size="sm" value={form.comments} onChange={(e) => set({ comments: e.target.value })} />
                </div>
              </td>
            </tr>,
          ]),
    ];
  }

  function viewRow(o) {
    const ov = overrun(o);
    const balance = Number(o.contract_given || 0) - Number(o.invoices_received || 0);
    const bd = showBreakdown ? breakdown[o.id] : null;
    // Legacy red markers (OrdersPage GetNonApprovedText): '*' = an unapproved
    // contractor invoice waits; '!' in front = its approval is BLOCKED (H&S not
    // settled / work not complete — the same gate the invoices page enforces).
    const approvable =
      Number(o.contract_given) > 0 &&
      (o.health_safety_na || (o.risk_assessment_uploaded && o.photos_uploaded)) &&
      o.work_complete;
    const marker = o.has_unapproved_invoice ? (approvable ? "*" : "!*") : null;
    return (
      <SortableTr key={o.id} id={o.id} disabled={dragDisabled}>
        <td className={numCls(o)}
            data-hint={o.check_overrun ? "Red = this order has an overrun awaiting checking (contractor invoices exceed the contract given). Tick Overrun Checked once reviewed." : undefined}>
          {o.order_number ?? o.seq}
        </td>
        <td>{o.work_centre_name || <span className="text-muted">—</span>}</td>
        <td>
          {/* Legacy red "NAME - NO PL": public liability missing or expired. */}
          {o.contractor_no_pl && o.contractor_name ? (
            <Hint label="This contractor's public liability is missing or expired (System Data → companies)">
              <span className="text-danger fw-semibold">{o.contractor_name} — NO PL</span>
            </Hint>
          ) : (
            o.contractor_name || <span className="text-muted">—</span>
          )}
        </td>
        {/* 1.0 habit: click the money and the tab becomes THAT page —
            contracts for allowance/given, invoices for received. With Show
            Order Break Down on, the lines render IN the column (1.0 layout,
            long text …-truncated; Comments column is hidden for the room). */}
        <td className="text-end text-nowrap clickable" role="button"
          data-hint="Open this order's contract lines (allowance + contract given live there)"
          onClick={() => openSubview(o.id, "contracts")}>
          {money(o.quoted_allowance)}
        </td>
        <td className="text-end clickable" role="button"
          style={bd ? { width: "34%", maxWidth: 0, minWidth: 220 } : undefined}
          data-hint="Open this order's contract lines (allowance + contract given live there)"
          onClick={() => openSubview(o.id, "contracts")}>
          {bd && bd.contracts.length > 0 ? (
            <>
              {bd.contracts.map((l) => (
                <div key={l.id} className="d-flex column-gap-2 small text-muted">
                  {/* minWidth 0 lets flex shrink the span so text-truncate can
                      ellipsize; otherwise it fills WHATEVER width the column
                      has — big screens show more, 1080p truncates sooner. */}
                  <span className="text-truncate text-start flex-grow-1" style={{ minWidth: 0 }} data-hint={l.description || undefined}>
                    {l.description || "(no description)"}
                  </span>
                  <span className="text-nowrap">{money(l.amount)}</span>
                </div>
              ))}
              <div className="text-nowrap fw-semibold border-top">{money(o.contract_given)}</div>
            </>
          ) : (
            <span className="text-nowrap">{money(o.contract_given)}</span>
          )}
        </td>
        <td className="text-end clickable" role="button"
          style={bd ? { maxWidth: 0, minWidth: 170 } : undefined}
          data-hint="Open this order's contractor invoices"
          onClick={() => openSubview(o.id, "invoices")}>
          {marker ? (
            <Hint label={marker === "!*" ? "Unapproved contractor invoice — approval BLOCKED (H&S / work complete)" : "Unapproved contractor invoice waiting"}>
              <span className="text-danger fw-bold me-1">{marker}</span>
            </Hint>
          ) : null}
          {bd && bd.invoices.length > 0 ? (
            <span className="d-inline-block align-top w-100">
              {bd.invoices.map((i) => (
                <span key={i.id} className="d-flex justify-content-between column-gap-2 small text-muted">
                  <span className="text-nowrap">{i.invoice_number || "—"}</span>
                  <span className="text-nowrap">{money(i.amount)}</span>
                </span>
              ))}
              <span className="d-block text-nowrap fw-semibold border-top text-end">{money(o.invoices_received)}</span>
            </span>
          ) : (
            <span className="text-nowrap">{money(o.invoices_received)}</span>
          )}
        </td>
        <td className="text-end text-nowrap">{money(balance)}</td>
        <td><Badge bg={ov.variant} text={ov.variant === "warning" ? "dark" : undefined}>{ov.text}</Badge></td>
        <td className="text-nowrap">
          {FLAGS.map(([k, label, Icon]) => (
            <Hint key={k} label={`${label}${o[k] ? "" : " — not set"}`}>
              <span className={`me-2 ${o[k] ? "text-success" : "text-body-tertiary opacity-50"}`}>
                <Icon />
              </span>
            </Hint>
          ))}
        </td>
        {showComments ? (
          <td className="text-truncate" style={{ maxWidth: 160 }} data-hint={o.comments || undefined}>
            {o.comments || <span className="text-muted">—</span>}
          </td>
        ) : null}
        <td className="text-end text-nowrap">
          {/* Same edit/delete buttons as the Progress tab (Robert 2026-07-16).
              No contracts icon — the money cells ARE the way in now. */}
          <Button variant="outline-primary" size="sm" data-hint="Edit order" aria-label="Edit order" onClick={() => startEdit(o)}>
            <FaPencilAlt />
          </Button>
          <Button variant="outline-danger" size="sm" className="ms-1" data-hint="Delete order" aria-label="Delete order" onClick={() => remove(o)}>
            <FaTrash />
          </Button>
        </td>
      </SortableTr>
    );
  }

  // Sub-page: the tab shows ONLY this order's contracts or invoices
  // (1.0's ContractGiven / InvoicesFromContractor popups, in-tab).
  const refreshAll = () => { load(); if (onChanged) onChanged(); };
  const subviewOrder = subview ? orders.find((x) => x.id === subview.orderId) : null;
  if (subview && subviewOrder) {
    return (
      <>
        <TabHeader
          title="Orders"
          help="orders"
          actions={
            <Button size="sm" variant="secondary" onClick={closeSubview}>← Back to orders</Button>
          }
        />
        {error ? <div className="alert alert-danger">{error}</div> : null}
        {subview.kind === "contracts" ? (
          <>
            <h6 className="mb-2">
              Contracts Given To: {subviewOrder.contractor_name || "—"}
              <span className="text-muted fw-normal"> · Order #{subviewOrder.order_number ?? subviewOrder.seq} — {subviewOrder.work_centre_name || "no work centre"}</span>
            </h6>
            <ContractsPanel claimId={claimId} orderId={subviewOrder.id} onChanged={refreshAll} />
          </>
        ) : (
          <>
            <h6 className="mb-2">Invoices From Contractor: {subviewOrder.contractor_name || "—"}</h6>
            <OrderHeader o={subviewOrder} />
            <InvoicesPanel claimId={claimId} orderId={subviewOrder.id} order={subviewOrder} onChanged={refreshAll} />
          </>
        )}
        <div className="mt-3">
          <Button size="sm" variant="secondary" onClick={closeSubview}>Close</Button>
        </div>
      </>
    );
  }

  return (
    <>
      <TabHeader
        title="Orders"
        help="orders"
        actions={
          <>
            {/* Open Phonebook — 1.0's OrdersPage `lbPhonebook`, which opens the
                phonebook filtered to the contractors on THIS job so you can ring
                round them without reading past two hundred others. Shown only
                when the job HAS orders, exactly as 1.0 gates it
                (`lbPhonebook.Visible = GPSOrders.Count > 0`) — on a job with
                nothing ordered it would filter to an empty list.
                An ICON with a tooltip, not the words "Open Phonebook" (Robert
                2026-08-11) — the row already carries two controls and a third
                worded one shouts louder than something used occasionally
                deserves. Same Hint-plus-icon-button pattern as the rest of the
                app. NOT outline-secondary: Bootswatch treats $secondary as a
                shade of the page (Zephyr's is #fff), so that button would be
                invisible in the default theme — see architecture.md §10.
                1.0 needed a "Back To Order Page" link because it navigated away;
                ours is an ordinary route, so the browser Back button covers it. */}
            {orders.length > 0 ? (
              <Hint label="Phonebook for this job's contractors">
                <Button
                  variant="outline-dark"
                  size="sm"
                  aria-label="Open the phonebook for this job's contractors"
                  onClick={() =>
                    navigate(`/reports/phonebook?claim=${claimId}`)
                  }
                >
                  <FaPhoneAlt />
                </Button>
              </Hint>
            ) : null}
            {/* Legacy: combine a contractor's invoice-less duplicate orders into one. */}
            {combinable.length > 0 && editingId === null ? (
              <>
                <ThemedSelect size="sm" style={{ width: 220 }} value={combineWith} onChange={(e) => setCombineWith(e.target.value)}>
                  <option value="">Combine orders for…</option>
                  {combinable.map((c) => (
                    <option key={c.contractor_company_id} value={c.contractor_company_id}>
                      {c.contractor_name} ({c.order_count})
                    </option>
                  ))}
                </ThemedSelect>
                {combineWith ? (
                  <Button size="sm" variant="outline-warning" onClick={combine}>Combine</Button>
                ) : null}
              </>
            ) : null}
            {/* Legacy: auto-create only while the job has quote lines and NO orders;
                shown disabled with the blocking reasons when the quote isn't ready. */}
            {autoCheck?.visible ? (
              <Button
                size="sm"
                variant="outline-primary"
                disabled={autoCreating || !autoCheck.allowed}
                aria-busy={autoCreating}
                data-hint={autoCheck.allowed ? undefined : autoCheck.blockers.join("; ")}
                onClick={autoCreate}
              >
                {autoCreating ? "Creating orders…" : "Auto-create from Quote"}
              </Button>
            ) : null}
            {editingId === null ? <Button size="sm" onClick={startAdd}>+ New Order</Button> : null}
          </>
        }
      />

      {autoCheck?.visible && !autoCheck.allowed ? (
        <div className="alert alert-warning py-2 small">
          Auto-create is disabled due to: {autoCheck.blockers.join(" · ")}
        </div>
      ) : null}
      {notice ? <div className="alert alert-info">{notice}</div> : null}
      {error ? <div className="alert alert-danger">{error}</div> : null}

      {/* The 1.0 top block, done once: the SAME Grand Totals as the Quote tab
          (receivable sell vs payable actuals — profit, variance), so the two
          tabs can never disagree (Robert 2026-07-16). */}
      <Card className="mb-3">
        <Card.Body>
          <GrandTotals claimId={claimId} reloadKey={totalsTick} />
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end mb-1">
        <Form.Check
          type="switch"
          id="orders-breakdown"
          label="Show Order Break Down"
          checked={showBreakdown}
          onChange={(e) => toggleBreakdown(e.target.checked)}
          data-hint="List each order's contract lines under its row (read-only — click through to edit)"
        />
      </div>

      <SortableRows items={orders} onReorder={handleReorder} disabled={dragDisabled}>
      <Table bordered hover responsive size="sm" className="mb-0 align-middle">
        <thead className="table-surface">
          <tr>
            <th style={{ width: 36 }} />
            <th style={{ width: 70 }}>Order #</th>
            <th>Work Centre</th>
            <th>Contractor</th>
            <th className="text-end">Quoted Allow.</th>
            <th className="text-end" style={showBreakdown ? { width: "34%" } : undefined}>Contract Given</th>
            <th className="text-end">Invoices Rec'd</th>
            <th className="text-end">Balance</th>
            <th>Overrun</th>
            <th style={{ width: 130 }}>Checklist</th>
            {showComments ? <th>Comments</th> : null}
            <th className="text-end" style={{ width: 70 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {orders.flatMap((o) => (editingId === o.id ? editorRow(o.id) : [viewRow(o)]))}
          {editingId === "new" ? editorRow("new") : null}
          {orders.length === 0 && editingId !== "new" ? (
            <tr><td colSpan={showComments ? 12 : 11} className="text-center text-muted">No orders. Use “Auto-create from Quote” to generate them.</td></tr>
          ) : null}
        </tbody>
        {orders.length > 0 ? (
          <tfoot>
            <tr className="table-surface fw-semibold">
              <td colSpan={4} className="text-end">Totals</td>
              <td className="text-end text-nowrap">{money(totals.allowance)}</td>
              <td className="text-end text-nowrap">{money(totals.given)}</td>
              <td className="text-end text-nowrap">{money(totals.received)}</td>
              <td className="text-end text-nowrap">{money(totals.given - totals.received)}</td>
              <td colSpan={3} />
            </tr>
          </tfoot>
        ) : null}
      </Table>
      </SortableRows>

      <div className="mt-3">
        <Form.Label className="mb-0 small fw-semibold">Order comments (job-level)</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          size="sm"
          value={orderComments}
          onChange={(e) => setOrderComments(e.target.value)}
          onBlur={saveOrderComments}
          placeholder="Comments that apply to this job's orders as a whole — saved when you click away."
        />
      </div>

      <DoAndChargePanel claimId={claimId} onChanged={onChanged} />
    </>
  );
}

export default ClaimOrdersTab;
